/**
 * Timezone utilities for ShopSoft
 * Handles all date/time formatting with the configured timezone
 */

// Common timezone options (matches backend)
export const TIMEZONE_OPTIONS = [
  { value: "America/Toronto", label: "Eastern Time (Toronto)", abbr: "EST/EDT" },
  { value: "America/New_York", label: "Eastern Time (New York)", abbr: "EST/EDT" },
  { value: "America/Chicago", label: "Central Time (Chicago)", abbr: "CST/CDT" },
  { value: "America/Denver", label: "Mountain Time (Denver)", abbr: "MST/MDT" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)", abbr: "PST/PDT" },
  { value: "America/Vancouver", label: "Pacific Time (Vancouver)", abbr: "PST/PDT" },
  { value: "America/Edmonton", label: "Mountain Time (Edmonton)", abbr: "MST/MDT" },
  { value: "America/Winnipeg", label: "Central Time (Winnipeg)", abbr: "CST/CDT" },
  { value: "America/Halifax", label: "Atlantic Time (Halifax)", abbr: "AST/ADT" },
  { value: "America/St_Johns", label: "Newfoundland Time (St. John's)", abbr: "NST/NDT" },
  { value: "UTC", label: "Coordinated Universal Time", abbr: "UTC" },
  { value: "Europe/London", label: "British Time (London)", abbr: "GMT/BST" },
  { value: "Europe/Paris", label: "Central European Time (Paris)", abbr: "CET/CEST" },
  { value: "Europe/Berlin", label: "Central European Time (Berlin)", abbr: "CET/CEST" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (Tokyo)", abbr: "JST" },
  { value: "Asia/Shanghai", label: "China Standard Time (Shanghai)", abbr: "CST" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (Sydney)", abbr: "AEST/AEDT" },
  { value: "Pacific/Auckland", label: "New Zealand Time (Auckland)", abbr: "NZST/NZDT" },
];

// Default timezone
const DEFAULT_TIMEZONE = "America/Toronto";

// Cached timezone from settings
let cachedTimezone = DEFAULT_TIMEZONE;

/**
 * Set the cached timezone (called when settings are loaded)
 */
export function setTimezone(tz) {
  cachedTimezone = tz || DEFAULT_TIMEZONE;
}

/**
 * Get the current timezone
 */
export function getTimezone() {
  return cachedTimezone;
}

/**
 * Get timezone abbreviation for display
 */
export function getTimezoneAbbr(date = new Date()) {
  try {
    const options = { timeZone: cachedTimezone, timeZoneName: 'short' };
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch {
    return '';
  }
}

/**
 * Format a date in the configured timezone
 * @param {Date|string} date - Date object or ISO string
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (default: true)
 * @param {boolean} options.includeDate - Include date in output (default: true)
 * @param {boolean} options.includeTimezone - Include timezone abbreviation (default: true)
 * @param {boolean} options.shortDate - Use short date format (default: false)
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
  if (!date) return 'N/A';
  
  const {
    includeTime = true,
    includeDate = true,
    includeTimezone = true,
    shortDate = false,
  } = options;
  
  try {
    let d;
    if (typeof date === 'string') {
      // If the ISO string doesn't have timezone info (no Z or +/-), assume UTC
      // This handles backend timestamps stored in UTC but returned without timezone indicator
      let dateStr = date;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.match(/\d{2}:\d{2}$/)) {
        dateStr = dateStr + 'Z';
      }
      d = new Date(dateStr);
    } else {
      d = date;
    }
    if (isNaN(d.getTime())) return 'Invalid Date';
    
    const formatOptions = {
      timeZone: cachedTimezone,
    };
    
    if (includeDate) {
      if (shortDate) {
        formatOptions.month = 'short';
        formatOptions.day = 'numeric';
      } else {
        formatOptions.year = 'numeric';
        formatOptions.month = 'short';
        formatOptions.day = 'numeric';
      }
    }
    
    if (includeTime) {
      formatOptions.hour = 'numeric';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
    }
    
    let formatted = new Intl.DateTimeFormat('en-US', formatOptions).format(d);
    
    if (includeTimezone && includeTime) {
      const abbr = getTimezoneAbbr(d);
      formatted = `${formatted} ${abbr}`;
    }
    
    return formatted;
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'N/A';
  }
}

/**
 * Format date only (no time)
 */
export function formatDateOnly(date, shortDate = false) {
  return formatDate(date, { includeTime: false, includeTimezone: false, shortDate });
}

/**
 * Format time only (no date)
 */
export function formatTimeOnly(date, includeTimezone = true) {
  return formatDate(date, { includeDate: false, includeTimezone });
}

/**
 * Format date for receipts/displays
 * Example: "Feb 12, 2026 08:02 AM EST"
 */
export function formatDateTime(date) {
  return formatDate(date, { includeTime: true, includeDate: true, includeTimezone: true });
}

/**
 * Get current date/time in the configured timezone
 */
export function now() {
  return new Date();
}

/**
 * Get start of today in the configured timezone
 */
export function todayStart() {
  const now = new Date();
  const todayStr = now.toLocaleDateString('en-CA', { timeZone: cachedTimezone });
  // Parse as local timezone date
  return new Date(todayStr + 'T00:00:00');
}

/**
 * Get end of today in the configured timezone
 */
export function todayEnd() {
  const now = new Date();
  const todayStr = now.toLocaleDateString('en-CA', { timeZone: cachedTimezone });
  return new Date(todayStr + 'T23:59:59.999');
}

/**
 * Check if a date is today in the configured timezone
 */
export function isToday(date) {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  
  const dStr = d.toLocaleDateString('en-CA', { timeZone: cachedTimezone });
  const todayStr = today.toLocaleDateString('en-CA', { timeZone: cachedTimezone });
  
  return dStr === todayStr;
}

/**
 * Get date string in YYYY-MM-DD format for the configured timezone
 */
export function toDateString(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-CA', { timeZone: cachedTimezone });
}

/**
 * Format relative time (e.g., "2 hours ago", "yesterday")
 */
export function formatRelative(date) {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  
  return formatDateOnly(d);
}

/**
 * Parse a date string treating it as the configured timezone
 */
export function parseInTimezone(dateStr, timeStr = '00:00:00') {
  if (!dateStr) return null;
  // Create date string and parse
  const fullStr = `${dateStr}T${timeStr}`;
  return new Date(fullStr);
}

export default {
  TIMEZONE_OPTIONS,
  setTimezone,
  getTimezone,
  getTimezoneAbbr,
  formatDate,
  formatDateOnly,
  formatTimeOnly,
  formatDateTime,
  now,
  todayStart,
  todayEnd,
  isToday,
  toDateString,
  formatRelative,
  parseInTimezone,
};
